import { AthleteProfileModel, BARatingFormula } from '@/models';
import { MissingDataBehaviour } from '@best-athletes/ba-types'
import { RecruitingProfileModel } from '@/models/athlete/RecruitingProfileModel';
import { isEmpty, isNotEmpty } from '@/pipes';

export class BARatingProfileModel extends RecruitingProfileModel {
	rating: number;
	scores: Array<number> = [];
	lowestScore: number = 1;
	highestScore: number = 100;
	defaultFormula: BARatingFormula = new BARatingFormula();
    isComputed: boolean = false;
	isCondensedDisplay: boolean = true;

	constructor(athleteProfile: AthleteProfileModel) {
		super(athleteProfile);
	}
	setRating(barating: BARatingProfileModel): this {
		this.rating = barating.rating;
		this.scores = barating.scores;
		this.lowestScore = barating.lowestScore;
		this.highestScore = barating.highestScore;
		this.isComputed = barating.isComputed;

		return this;
	}

    get IsComputed(): boolean {
        return this.isComputed;
    }
	get Rating(): string {
		if( isEmpty(this.rating) ) return 'na';
		return this.rating.toFixed(0);
	}
	getData( obj: BARatingProfileModel, key: string ): number {
		if( isEmpty(obj) ) return undefined;

		const keys = key.split('.');
		if( (keys.length === 1) && isNotEmpty(obj[keys[0]])) {
			const data = obj[keys[0]];
			if( typeof(data) === 'number' ) return data;
			return Number(data);
		}

		return this.getData(obj[keys[0]], keys.slice(1).join('.'))
	}

	norm(value: number, valueMin: number, valueMax: number, normMin: number = 1, normMax: number = 100): number {
		const norm0to1 = (value-valueMin)/(valueMax-valueMin);
		return norm0to1 * (normMax - normMin) + normMin;
	}
	compute(formula: BARatingFormula): number {
		let maxScore: number = 0;

		// compute score for each factor
		// score(x) = x*a*a+x*b+c where x is between 0, 1
		this.scores = formula.factors.map(factor => {
			let data = this.getData(this, factor.key);

			// if factor is mis-constructed, ignore this factor
			if( factor.max <= factor.min ) return undefined;

			// handle missing data
			// default: athlete is assigned a default value
			// zero: athlete is penalized for not having this attribute with a 0 score
			// ignore: ignore this factor in computation
			if( isEmpty(data) ) {
				if( factor.missingBehaviour === MissingDataBehaviour.default ) data = factor.default;
				else if( factor.missingBehaviour === MissingDataBehaviour.zero ) data = 0;
				else if( factor.missingBehaviour === MissingDataBehaviour.ignore ) return undefined;
				else return undefined;
			}

			// constrain the data between min and max
			if( data < factor.min ) data = factor.min;
			if( data > factor.max ) data = factor.max;

			// normalize data between lowestScore and highestScore
			const norm = this.norm(data, factor.min, factor.max, this.lowestScore, this.highestScore);

			// compute the score for this factor
			const score = norm * factor.a * factor.a + norm * factor.b + factor.c;
			const maxFactorScore = this.highestScore * factor.a * factor.a + this.highestScore * factor.b + factor.c;
			maxScore = maxScore + maxFactorScore;
			return score;
		})

		// remove any unavailable scores
		const availableScores = this.scores.filter(s => s != undefined);
		if( availableScores.length === 0 ) return undefined;

		// compute the final rating as a percentage between 0 and maxScore
        this.isComputed = true;
		return this.rating = this.norm((availableScores.reduce((acc, value) => acc + value, 0)), 0, maxScore, this.lowestScore, this.highestScore);
	}

	ToggleFactorsDisplay() {
		this.isCondensedDisplay = !this.isCondensedDisplay;
	}
}

